import React, { useCallback, useState } from "react"

import {
	Grid,
	TextField,
	Tooltip,
	Typography
} from "@material-ui/core"
import {
	InfoOutlined as InfoIcon
} from "@material-ui/icons"

import useRDStationCRMApi from "@/hooks/useRDStationCRMApi"
import useContentEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/hooks/useContentEditor"

import BlockBodySectionEditor from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/BlockBodySectionEditor"
import SelectInput from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/components/SelectInput"
import { Notification } from "@/components"

import {
	getCurrentRDStationDeal
} from "@/pages/Admin/Flow/FlowConstructor/FlowEditor/utils/chatBotFlowBlockContent"

import {
	RDStationCRMDeal,
	RDStationCRMPipeline,
	RDStationCRMStage
} from "@/protocols/RdStationPluginProtocol"
import { DeepPartial } from "@/protocols/utility"
import { ChatBotFlowBlockContent } from "@/protocols/chatBot"

import RDStationCRMService from "@/services/RDStationCRM"

import { colors } from "@/styles/colors"

type DealOptionsSectionEditorProps = {
	onSave: (chatBotFlowBlockContent: ChatBotFlowBlockContent) => void
	chatBotFlowBlockContent: ChatBotFlowBlockContent
}

const DealOptionsSectionEditor: React.FC<DealOptionsSectionEditorProps> = (props) => {
	const {
		onSave,
		chatBotFlowBlockContent,
		children
	} = props

	const {
		getPipelineAndStageTitle,
		validatePlugin
	} = useRDStationCRMApi()

	const contentEditor = useContentEditor({ chatBotFlowBlockContent })
	const currentDeal = getCurrentRDStationDeal(contentEditor.content)

	const [pipelines, setPipelines] = useState<RDStationCRMPipeline[]>([])

	const getPipelinesRDStation = useCallback(async () => {
		const isValidPlugin = await validatePlugin()

		if (isValidPlugin) {
			try {
				const responseData = await RDStationCRMService.getPipelines()
				setPipelines(responseData)
			} catch (error) {
				Notification.error({ message: "Não foi possível carregar os funis de vendas." })
			}
		}
	}, [pipelines])

	const {
		pipelineTitle,
		stageTitle
	} = getPipelineAndStageTitle(currentDeal as RDStationCRMDeal)

	const handleOpen = () => {
		contentEditor.reset()
	}

	const handleSave = () => {
		onSave(contentEditor.content)
	}

	const handleFilterPipelineStages = (pipelineTitle: string): RDStationCRMStage[] => {
		if (pipelineTitle === "") {
			return []
		}

		const selectedPipeline = pipelines?.find((pipeline) => pipeline.title === pipelineTitle)

		return selectedPipeline?.stages || []
	}

	const handleChangeDealContent = (deal: DeepPartial<RDStationCRMDeal>) => {
		contentEditor.changeContent({
			rdStationDeal: deal
		})
	}

	return (
		<BlockBodySectionEditor
			title="SELECIONE UM FUNIL DE VENDAS E UMA ETAPA PARA SALVAR O NEGÓCIO"
			onSave={handleSave}
			onOpen={handleOpen}
			AsideDrawerProps={{
				children: (
					<Grid
						container
						direction="row"
						spacing={2}
					>
						<Grid
							item
							xs={12}
						>
							<Grid
								container
								direction="column"
								spacing={1}
							>
								<Grid
									item
								>
									<Grid
										container
										alignItems="center"
										spacing={1}
									>
										<Grid
											item
										>
											<Typography variant="h4">Funil de vendas</Typography>
										</Grid>
										<Grid
											item
										>
											<Grid
												container
												alignItems="center"
											>
												<Tooltip title="Serão exibidos apenas os funis de vendas retornados pela API do RD Station CRM.">
													<InfoIcon
														htmlColor={colors.gray[400]}
														style={{
															width: 16,
															height: 16
														}}
													/>
												</Tooltip>
											</Grid>
										</Grid>
									</Grid>
								</Grid>

								<Grid
									item
								>
									<SelectInput
										defaultOption={{
											title: pipelineTitle
										}}
										onChange={option => {
											handleChangeDealContent({
												pipeline: {
													id: option.id,
													title: option.title
												},
												stage: {
													title: ""
												}
											})
										}}
										options={pipelines}
										placeholder="Selecione um Funil de Vendas"
										getOptionSelected={(option, value) => option.title === value.title}
										getOptionLabel={(option) => option.title}
										onUpdate={getPipelinesRDStation}
									/>
								</Grid>
							</Grid>
						</Grid>

						<Grid
							item
							xs={12}
						>
							<Grid container direction="column" spacing={1}>
								<Grid item xs={12}>
									<Typography variant="h4">Etapa</Typography>
								</Grid>
								<Grid item xs={12}>
									<SelectInput
										value={{
											title: stageTitle
										}}
										onChange={option => (
											handleChangeDealContent({
												stage: {
													...option
												}
											})
										)}
										options={handleFilterPipelineStages(pipelineTitle)}
										placeholder="Selecione uma Etapa"
										getOptionSelected={(option, value) => option.title === value.title}
										getOptionLabel={(option) => option.title}
									/>
								</Grid>
							</Grid>
						</Grid>

						<Grid
							item
							xs={12}
						>
							<Grid
								container
								direction="column"
								spacing={1}
							>
								<Grid
									item
								>
									<Typography variant="h4">Nome do negócio</Typography>
								</Grid>

								<Grid
									item
								>
									<TextField
										variant="outlined"
										placeholder="Ex: Negociação com o cliente"
										value={currentDeal?.name || ""}
										onChange={({ target }) => (
											handleChangeDealContent({
												name: target.value
											})
										)}
										fullWidth
									/>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				)
			}}
		>
			{children}
		</BlockBodySectionEditor>
	)
}

export default DealOptionsSectionEditor
